import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Bookmark } from 'lucide-react';

const ATTRIBUTE_ROWS = ['rating', 'quality', 'durability', 'value', 'features'];

const ComparisonResultModal = ({ comparisonResult, onClose, onViewHistory, onToggleBookmark }) => {
  const { t } = useTranslation();
  const [isBookmarked, setIsBookmarked] = useState(comparisonResult.isBookmarked);

  useEffect(() => {
    setIsBookmarked(comparisonResult.isBookmarked);
  }, [comparisonResult.isBookmarked]);

  const attributeLabels = useMemo(
    () => ({
      rating: t('ai_compare.attribute_rating'),
      quality: t('ai_compare.attribute_quality'),
      durability: t('ai_compare.attribute_durability'),
      value: t('ai_compare.attribute_value'),
      features: t('ai_compare.attribute_features'),
    }),
    [t],
  );

  const handleBookmark = () => {
    const nextValue = !isBookmarked;
    setIsBookmarked(nextValue);
    if (onToggleBookmark) {
      onToggleBookmark({ ...comparisonResult, isBookmarked: nextValue });
    }
  };

  return (
    <div className='ai-compare-modal-overlay' role='dialog' aria-modal='true'>
      <div className='ai-compare-modal ai-compare-modal--wide'>
        <button type='button' className='ai-compare-modal__close' onClick={onClose} aria-label={t('ai_compare.close')}>
          ×
        </button>

        <h2 className='modal-title'>{t('ai_compare.results_title')}</h2>

        {comparisonResult.context && (
          <div className='comparison-context'>
            <span className='context-label'>{t('ai_compare.context_label')}:</span>
            <p className='context-value'>{comparisonResult.context}</p>
          </div>
        )}

        <div className='comparison-table'>
          <table>
            <thead>
              <tr>
                <th>{t('ai_compare.attribute_column')}</th>
                {comparisonResult.products.map(product => {
                  const truncatedName = product.name.length > 50 ? `${product.name.substring(0, 50)}...` : product.name;
                  return (
                    <th key={product.id}>
                      {product.image ? (
                        <img src={product.image} alt='' className='product-thumb' />
                      ) : (
                        <div className='product-thumb product-thumb--placeholder' />
                      )}
                      <div className='product-name' title={product.name}>
                        {truncatedName}
                      </div>
                      {product.displayPrice && <div className='product-price'>{product.displayPrice}</div>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {ATTRIBUTE_ROWS.map(rowKey => (
                <tr key={rowKey}>
                  <td>{attributeLabels[rowKey]}</td>
                  {comparisonResult.products.map(product => {
                    const value = product.attributes?.[rowKey];
                    if (rowKey === 'rating' && value) {
                      return <td key={`${product.id}-${rowKey}`}>{Number(value).toFixed(1)}/5</td>;
                    }
                    return <td key={`${product.id}-${rowKey}`}>{value || '—'}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className='verdict-box'>
          <div className='verdict-text'>{comparisonResult.verdict}</div>
        </div>

        <div className='modal-footer modal-footer--results'>
          <button type='button' className='button-icon' onClick={handleBookmark}>
            <Bookmark size={18} fill={isBookmarked ? 'currentColor' : 'none'} />
            {t('ai_compare.bookmark')}
          </button>
          <button type='button' className='button-secondary' onClick={onClose}>
            {t('ai_compare.close')}
          </button>
          <button type='button' className='button-primary' onClick={onViewHistory}>
            {t('ai_compare.view_history')}
          </button>
        </div>
      </div>
    </div>
  );
};
ComparisonResultModal.propTypes = {
  comparisonResult: PropTypes.shape({
    id: PropTypes.string.isRequired,
    products: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        image: PropTypes.string,
        displayPrice: PropTypes.string,
        attributes: PropTypes.object,
      }),
    ).isRequired,
    context: PropTypes.string,
    verdict: PropTypes.string,
    isBookmarked: PropTypes.bool,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onViewHistory: PropTypes.func.isRequired,
  onToggleBookmark: PropTypes.func,
};

ComparisonResultModal.defaultProps = {
  onToggleBookmark: null,
};

export default ComparisonResultModal;
